import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import AuthProvider from './components/AuthProvider';
import { SystemSettingsProvider } from './contexts/SystemSettingsContext';
import Layout from './components/Layout';
import GuestLayout from './components/GuestLayout';
import AdminLayout from './components/AdminLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Search from './pages/Search';
import Charts from './pages/Charts';
import ChartView from './pages/ChartView';
import MyCharts from './pages/MyCharts';
import DataVisualization from './pages/DataVisualization';
import ProducerPrices from './pages/ProducerPrices';
import TradeAnalytics from './pages/TradeAnalytics';
import AnalyticsHome from './pages/AnalyticsHome';
import EleavingLabHome from './pages/EleavingLabHome';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminPlatform from './pages/AdminPlatform';
import ElasticsearchAdmin from './pages/ElasticsearchAdmin';
import AllSettings from './components/ImportSettings';
import MaintenanceCheck from './components/MaintenanceCheck';
import ProtectedRoute from './components/ProtectedRoute';
import ChartCreate from './pages/ChartCreate';
import AdminRoute from './components/AdminRoute';
import RegistrationRoute from './components/RegistrationRoute';
import ProjectsPage from './pages/ProjectsPage';
import ProjectInterfacePage from './pages/ProjectInterfacePage';
import UserLayout from './components/UserLayout';
import TeamInviteAccept from './pages/TeamInviteAccept';
import BusinessDirectory from './pages/BusinessDirectory';
import BusinessDetail from './pages/BusinessDetail';
import EditBusiness from './pages/EditBusiness';
import CreateBusiness from './pages/CreateBusiness';
import TaskCreationPage from './pages/TaskCreationPage';
import GanttPage from './pages/GanttPage';
import TeamTasksPage from './pages/TeamTasksPage';
import TimelinePage from './pages/TimelinePage';
import TeamProjectCollaborationPage from './pages/TeamProjectCollaborationPage';
import PlanningPokerPage from './pages/PlanningPokerPage';
import VideoCallPage from './components/VideoCallPage';
import VideoCallPageWrapper from './components/VideoCallPageWrapper';
import ProjectFileCollaborationPage from './pages/ProjectFileCollaborationPage';
import VaultSpherePage from './pages/VaultSpherePage';
import Forum from './pages/Forum';
import CategoryPosts from './pages/CategoryPosts';
import PostDetail from './pages/PostDetail';
import NewsPage from './pages/NewsPage';
import CreateNewsPage from './pages/CreateNewsPage';
import EditNewsPage from './pages/EditNewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import Actualities from './pages/Actualities';
import Courses from './pages/Courses';
import CourseCalendar from './pages/CourseCalendar';
import CourseDetail from './pages/CourseDetail';
import MyCourses from './pages/MyCourses';
import CreateCourse from './pages/CreateCourse';
import PublicEventsPage from './pages/PublicEventsPage';
import EventDetailPage from './pages/EventDetailPage';
import AdminEvents from './pages/AdminEvents';
import AdminBackupRestorePage from './pages/AdminBackupRestorePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import EditProfile from './pages/EditProfile';

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <SystemSettingsProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <MaintenanceCheck>
              <div className="min-h-screen bg-gray-50">
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/register" element={
                    <RegistrationRoute>
                      <Register />
                    </RegistrationRoute>
                  } />
                  
                  {/* Team invite acceptance - requires auth */}
                  <Route path="/teams/accept/:token" element={
                    <ProtectedRoute>
                      <TeamInviteAccept />
                    </ProtectedRoute>
                  } />

                  {/* Guest routes - accessible to all users */}
                  <Route path="/" element={<GuestLayout />}>
                    <Route index element={<EleavingLabHome />} />
                    <Route path="home" element={<Home />} />
                    <Route path="search" element={<Search />} />
                    <Route path="analytics" element={<AnalyticsHome />} />
                    <Route path="my-charts" element={<MyCharts />} />
                    <Route path="visualization" element={<DataVisualization />} />
                    <Route path="producer-prices" element={<ProducerPrices />} />
                    <Route path="trade-analytics" element={<TradeAnalytics />} />
                     <Route path="businesses" element={<BusinessDirectory />} />
                    <Route path="businesses/slug/:slug" element={<BusinessDetail />} />
                    <Route path="businesses/:id" element={<BusinessDetail />} />
                    <Route path="forum" element={<Forum />} />
                    <Route path="forum/category/:categoryName" element={<CategoryPosts />} />
                    <Route path="forum/post/:postId" element={<PostDetail />} />
                    <Route path="news" element={<NewsPage />} />
                    <Route path="actualities" element={<Actualities />} />
                    <Route path="news/add" element={<CreateNewsPage />} />
                    <Route path="news/edit/:id" element={<EditNewsPage />} />
                    <Route path="news/:id" element={<NewsDetailPage />} />
                    <Route path="courses" element={<Courses />} />
                    <Route path="courses/calendar" element={<CourseCalendar />} />
                    <Route path="courses/:id" element={<CourseDetail />} />
                    <Route path="events" element={<PublicEventsPage />} />
                    <Route path="events/:id" element={<EventDetailPage />} />
                    <Route path="about" element={<AboutPage />} />
                    <Route path="contact" element={<ContactPage />} />
                  </Route>
                  
                  {/* Authenticated routes - require login */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <UserLayout />
                      </ProtectedRoute>
                    }
                  >
                    {/* Projects routes */}
                    <Route path="projects">
                      <Route index element={<ProjectsPage />} />
                      <Route path=":projectId" element={<ProjectInterfacePage />} />
                      <Route path=":projectId/timeline" element={<TimelinePage />} />
                      <Route path=":projectId/team/:teamId/collaboration" element={<TeamProjectCollaborationPage />} />
                      <Route path=":projectId/team/:teamId/planning-poker" element={<PlanningPokerPage />} />
                      <Route path=":projectId/file-collaboration" element={<ProjectFileCollaborationPage />} />
                    </Route>

                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="profile" element={<EditProfile />} />
                    <Route path="dashboard/create-business" element={<CreateBusiness />} />
                    <Route path="my-courses" element={<MyCourses />} />
                    <Route path="create-course" element={<CreateCourse />} />
                    <Route path="vaultsphere" element={<VaultSpherePage />} />

                    {/* Routes pour les nouvelles sections de navigation */}
                    <Route path="inbox" element={<Dashboard />} />
                    <Route path="reports" element={<Dashboard />} />
                    <Route path="portfolios" element={<Dashboard />} />
                    <Route path="objectives" element={<Dashboard />} />
                    <Route path="workspace" element={<Dashboard />} />

                    <Route path="/tasks/:projectId?" element={<TeamTasksPage />} />
                  </Route>

                  {/* Charts list and view use app Layout; create/edit standalone without sidebar */}
                  <Route path="/charts" element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Charts />} />
                    <Route path=":id" element={<ChartView />} />
                  </Route>

                  {/* Standalone full-screen routes for create/edit (no sidebar) */}
                  <Route path="/charts/create" element={
                    <ProtectedRoute>
                      <ChartCreate />
                    </ProtectedRoute>
                  } />
                  <Route path="/charts/:id/edit" element={
                    <ProtectedRoute>
                      <ChartCreate />
                    </ProtectedRoute>
                  } />
                  
                  {/* Business edit route - require authentication */}
                  <Route path="/businesses/:id/edit" element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<EditBusiness />} />
                  </Route>
                  
                  {/* Admin routes - require authentication and admin role */}
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout />
                        
                      </AdminRoute>
                    </ProtectedRoute>
                  }>
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="platform" element={<AdminPlatform />} />
                    <Route path="events" element={<AdminEvents />} />
                    <Route path="elasticsearch" element={<ElasticsearchAdmin />} />
                    <Route path="backup-restore" element={<AdminBackupRestorePage />} />
                    <Route path="all-settings" element={<AllSettings />} />
                  </Route>
                  <Route path="/video-call" element={
                    <ProtectedRoute>
                      <VideoCallPageWrapper />
                    </ProtectedRoute>
                  } />
                </Routes>
              </div>
            </MaintenanceCheck>
          </Router>
        </SystemSettingsProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
